import { debounce } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import { useMemo, useState } from 'react';

import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@mui/material';

import { useTranslate } from 'src/locales';
import {
  useGetDealCompanyLinksQuery,
  useGetDealContactLinksQuery,
  useSaveDocumentToLinkMutation
} from 'src/services/deal-flow/deal-flow.service';
import { IDealCompanyLink, IDealContactLink } from 'src/services/deal-flow/deal-flow.types';

// ----------------------------------------------------------------------

interface DealDocumentSaverProps {
  open: boolean;
  onClose: () => void;
  dealDocId: string;
  dealId: string;
}

enum TabValue {
  CONTACT = "contact",
  COMPANY = "company"
}

export default function DealDocumentSaver({ open, onClose, dealDocId, dealId }: DealDocumentSaverProps) {
  const { t } = useTranslate();

  const [tab, setTab] = useState<TabValue>(TabValue.CONTACT);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedContactLink, setSelectedContactLink] = useState<IDealContactLink | null>(null);
  const [selectedCompanyLink, setSelectedCompanyLink] = useState<IDealCompanyLink | null>(null);

  const [saveDocumentToLink, { isLoading: isSaving }] = useSaveDocumentToLinkMutation();

  // Fetch contact links
  const {
    data: contactLinksData,
    isLoading: isLoadingContacts
  } = useGetDealContactLinksQuery({
    dealId,
    page: 1,
    perPage: 100,
    searchQuery
  }, { skip: !dealId || tab !== TabValue.CONTACT });

  // Fetch company links
  const {
    data: companyLinksData,
    isLoading: isLoadingCompanies
  } = useGetDealCompanyLinksQuery({
    dealId,
    page: 1,
    perPage: 100,
    searchQuery
  }, { skip: !dealId || tab !== TabValue.COMPANY });

  const contactLinks = contactLinksData?.results || [];
  const companyLinks = companyLinksData?.results || [];

  const handleTabChange = (_event: React.SyntheticEvent, newValue: TabValue) => {
    setTab(newValue);
    setSearchQuery('');
    setSelectedContactLink(null);
    setSelectedCompanyLink(null);
  };

  const onSearchQueryChange = useMemo(
    () => debounce((value: string) => {
      setSearchQuery(value);
    }, 500),
    [setSearchQuery]
  );

  const handleSave = async () => {
    try {
      if (tab === TabValue.CONTACT && selectedContactLink) {
        await saveDocumentToLink({
          dealId,
          dealDocId,
          data: {
            link_type: 'contact',
            link_id: selectedContactLink.id
          }
        }).unwrap();

        enqueueSnackbar(t('deal_flow.document_saver.success_contact'), { variant: 'success' });
        onClose();
      } else if (tab === TabValue.COMPANY && selectedCompanyLink) {
        await saveDocumentToLink({
          dealId,
          dealDocId,
          data: {
            link_type: 'company',
            link_id: selectedCompanyLink.id
          }
        }).unwrap();

        enqueueSnackbar(t('deal_flow.document_saver.success_company'), { variant: 'success' });
        onClose();
      }
    } catch (error) {
      enqueueSnackbar(t('deal_flow.document_saver.error'), { variant: 'error' });
    }
  };

  const isDisabled = (tab === TabValue.CONTACT && !selectedContactLink) || (tab === TabValue.COMPANY && !selectedCompanyLink) || isSaving;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('deal_flow.document_saver.title')}</DialogTitle>

      <DialogContent>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={tab} onChange={handleTabChange}>
            <Tab label={t('deal_flow.document_saver.tab_contact')} value={TabValue.CONTACT} />
            <Tab label={t('deal_flow.document_saver.tab_company')} value={TabValue.COMPANY} />
          </Tabs>
        </Box>

        {tab === TabValue.CONTACT && (
          <Stack spacing={2}>
            <Typography variant="body2">
              {t('deal_flow.document_saver.select_contact')}
            </Typography>

            <Autocomplete
              fullWidth
              value={selectedContactLink}
              onChange={(_event, newValue) => {
                setSelectedContactLink(newValue);
              }}
              onInputChange={(_event, value) => onSearchQueryChange(value)}
              options={contactLinks}
              getOptionLabel={(option) => `${option.contact.first_name} ${option.contact.last_name}`}
              loading={isLoadingContacts}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('deal_flow.document_saver.contact_label')}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoadingContacts ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Stack>
                    <Typography variant="body2">
                      {option.contact.first_name} {option.contact.last_name}
                    </Typography>
                    {option.contact.email && (
                      <Typography variant="caption" color="text.secondary">
                        {option.contact.email}
                      </Typography>
                    )}
                  </Stack>
                </li>
              )}
            />
          </Stack>
        )}

        {tab === TabValue.COMPANY && (
          <Stack spacing={2}>
            <Typography variant="body2">
              {t('deal_flow.document_saver.select_company')}
            </Typography>

            <Autocomplete
              fullWidth
              value={selectedCompanyLink}
              onChange={(_event, newValue) => {
                setSelectedCompanyLink(newValue);
              }}
              onInputChange={(_event, value) => onSearchQueryChange(value)}
              options={companyLinks}
              getOptionLabel={(option) => option.company_profile.company_name}
              loading={isLoadingCompanies}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('deal_flow.document_saver.company_label')}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoadingCompanies ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Typography variant="body2">
                    {option.company_profile.company_name}
                  </Typography>
                </li>
              )}
            />
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="inherit">
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isDisabled}
          startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {t('deal_flow.document_saver.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}